// WbPriceBandDetailPage — /weighbridge/pricing/bands/:enc_id (and /new).
//
// Phase C4 Group 1. Create + edit form for a single price band.
//
// Form shape branches on band type:
//   - manual:     Identity + (when editing) inline product-lines editor.
//                 Lines are persisted via a single bulk-PUT on Save.
//   - calculated: Identity + a formula block (calc_op + calc_value). No
//                 lines section. A live "preview" line shows the formula
//                 applied to a fixed £10.00 base.
//
// PATCH is deliberately narrow: server returns 422 if `type` is sent
// during an update (and silently rejects `calc_op`/`calc_value`), so the
// update path strips those keys entirely. See useWbPriceBands.ts.
//
// General Public band: name and lines may still be edited, but the
// `is_default_for_new_customers` toggle is hidden (the GP band IS the
// default fallback) and Delete is hidden (server refuses with 409).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Modal } from '../../../components/ui/Modal/Modal';
import { apiClient } from '../../../api/client';
import {
  useCreatePriceBand,
  useDeletePriceBand,
  useReplacePriceBandLines,
  useUpdatePriceBand,
  useWbPriceBand,
  type CalcOp,
  type CreatePriceBandInput,
  type PriceBandType,
  type UpdatePriceBandInput,
} from '../hooks/useWbPriceBands';
import type { WbProduct } from '../hooks/useWbProducts';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbPriceBandDetailPage.module.css';

const CALC_OP_OPTIONS: ReadonlyArray<{ value: CalcOp; label: string }> = [
  { value: 'plus_amount', label: 'Add fixed amount (£)' },
  { value: 'minus_amount', label: 'Subtract fixed amount (£)' },
  { value: 'plus_percent', label: 'Add percentage (%)' },
  { value: 'minus_percent', label: 'Subtract percentage (%)' },
];

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

interface LineRow {
  product_enc_id: string;
  product_name: string;
  product_code: string;
  /** Local input value as a string so partial entries don't get coerced
   *  through NaN. Validated to a finite number on Save. */
  price: string;
}

interface FormState {
  name: string;
  code: string;
  type: PriceBandType;
  calc_op: CalcOp;
  calc_value: string;
  is_default_for_new_customers: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  code: '',
  type: 'manual',
  calc_op: 'plus_amount',
  calc_value: '',
  is_default_for_new_customers: false,
};

// -- Sub-components ---------------------------------------------------------

interface TypeSelectorProps {
  value: PriceBandType;
  onChange: (next: PriceBandType) => void;
}

function TypeSelector({ value, onChange }: TypeSelectorProps): JSX.Element {
  return (
    <div className={styles.typeSelector} role="radiogroup" aria-label="Band type">
      <button
        type="button"
        role="radio"
        aria-checked={value === 'manual'}
        aria-pressed={value === 'manual'}
        className={
          value === 'manual'
            ? `${styles.typeCard} ${styles.typeCardActive}`
            : styles.typeCard
        }
        onClick={() => onChange('manual')}
      >
        <span className={styles.typeCardTitle}>Manual</span>
        <span className={styles.typeCardBody}>
          Fixed prices per product. You set each product&apos;s price directly.
        </span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === 'calculated'}
        aria-pressed={value === 'calculated'}
        className={
          value === 'calculated'
            ? `${styles.typeCard} ${styles.typeCardActive}`
            : styles.typeCard
        }
        onClick={() => onChange('calculated')}
      >
        <span className={styles.typeCardTitle}>Calculated</span>
        <span className={styles.typeCardBody}>
          Prices derived from a formula applied to the product default price.
        </span>
      </button>
    </div>
  );
}

interface FormulaPreviewProps {
  op: CalcOp;
  value: string;
}

function FormulaPreview({ op, value }: FormulaPreviewProps): JSX.Element {
  const num = value.trim() === '' ? NaN : Number(value);
  let result: string;
  if (!Number.isFinite(num)) {
    result = '—';
  } else {
    const base = 10;
    let raw: number;
    switch (op) {
      case 'plus_amount':
        raw = base + num;
        break;
      case 'minus_amount':
        raw = base - num;
        break;
      case 'plus_percent':
        raw = base * (1 + num / 100);
        break;
      case 'minus_percent':
        raw = base * (1 - num / 100);
        break;
    }
    result = GBP.format(raw);
  }
  return (
    <p className={styles.preview} aria-live="polite">
      e.g. product default £10.00 →{' '}
      <span className={styles.previewValue}>{result}</span>
    </p>
  );
}

// -- Main page --------------------------------------------------------------

export function WbPriceBandDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const bandQuery = useWbPriceBand(isNew ? undefined : enc_id);
  const createMut = useCreatePriceBand();
  const updateMut = useUpdatePriceBand(isNew ? '' : (enc_id as string));
  const deleteMut = useDeletePriceBand(isNew ? '' : (enc_id as string));
  const linesMut = useReplacePriceBandLines(isNew ? '' : (enc_id as string));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const band = bandQuery.data?.price_band;
  const isGeneralPublic = band?.is_general_public ?? false;

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    if (!band) return;
    setForm({
      name: band.name,
      code: band.code,
      type: band.type,
      // Fall back to a sensible default for the calc fields when the band
      // is manual — keeps the form state shape stable.
      calc_op: band.calc_op ?? 'plus_amount',
      calc_value: band.calc_value !== null ? String(band.calc_value) : '',
      is_default_for_new_customers: band.is_default_for_new_customers,
    });
    if (band.type === 'manual' && Array.isArray(band.lines)) {
      setLines(
        band.lines.map((l) => ({
          product_enc_id: l.product.enc_id,
          product_name: l.product.name,
          product_code: l.product.code,
          price: String(l.price),
        })),
      );
    } else {
      setLines([]);
    }
  }, [isNew, band]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (form.name.length > 200)
      errs['name'] = 'Name must be 200 characters or fewer.';
    if (!form.code.trim()) errs['code'] = 'Code is required.';
    if (form.code.length > 50)
      errs['code'] = 'Code must be 50 characters or fewer.';
    if (form.type === 'calculated') {
      if (form.calc_value.trim() === '') {
        errs['calc_value'] = 'A value is required.';
      } else {
        const n = Number(form.calc_value);
        if (!Number.isFinite(n) || n < 0) {
          errs['calc_value'] = 'Value must be a non-negative number.';
        }
      }
    }
    // Line price validation — only relevant for existing manual bands
    // since lines are not sent on create.
    if (!isNew && form.type === 'manual') {
      lines.forEach((row, idx) => {
        if (row.price.trim() === '') {
          errs[`line:${idx}`] = 'Price required.';
          return;
        }
        const n = Number(row.price);
        if (!Number.isFinite(n) || n < 0) {
          errs[`line:${idx}`] = 'Price must be ≥ 0.';
        }
      });
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function buildCreateBody(): CreatePriceBandInput {
    const body: CreatePriceBandInput = {
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type,
      is_default_for_new_customers: form.is_default_for_new_customers,
    };
    if (form.type === 'calculated') {
      body.calc_op = form.calc_op;
      body.calc_value = Number(form.calc_value);
    }
    return body;
  }

  /** Strictly excludes `type`, `calc_op`, `calc_value`, `is_general_public`.
   *  Sending `type` would 422; the calc fields are immutable post-create.
   */
  function buildUpdateBody(): UpdatePriceBandInput {
    return {
      name: form.name.trim(),
      code: form.code.trim(),
      is_default_for_new_customers: form.is_default_for_new_customers,
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    if (isNew) {
      const body = buildCreateBody();
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/pricing/bands'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
      return;
    }

    // Existing band: PATCH first, then PUT lines if manual.
    const updateBody = buildUpdateBody();
    try {
      await updateMut.mutateAsync(updateBody);
    } catch (err) {
      setError(extractErrorMessage(err));
      return;
    }

    if (form.type === 'manual') {
      const lineInputs = lines.map((l) => ({
        product_enc_id: l.product_enc_id,
        price: Number(l.price),
      }));
      try {
        await linesMut.mutateAsync(lineInputs);
      } catch (err) {
        // PATCH succeeded; lines failed. Stay on the page so the operator
        // can retry rather than silently losing the line edits.
        setError(extractErrorMessage(err));
        return;
      }
    }

    navigate('/weighbridge/pricing/bands');
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        navigate('/weighbridge/pricing/bands');
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  function handleAddLine(product: WbProduct) {
    setLines((prev) => [
      ...prev,
      {
        product_enc_id: product.enc_id,
        product_name: product.name,
        product_code: product.code,
        price: String(product.default_general_public_price),
      },
    ]);
    setPickerOpen(false);
  }

  function handleRemoveLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleLinePriceChange(idx: number, value: string) {
    setLines((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, price: value } : row)),
    );
  }

  const existingProductIds = useMemo(
    () => new Set(lines.map((l) => l.product_enc_id)),
    [lines],
  );

  const isSaving =
    createMut.isPending || updateMut.isPending || linesMut.isPending;
  const isLoadingExisting = !isNew && bandQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Price Band' : form.name || 'Edit Price Band'}
        subtitle={
          isNew ? 'Create a new price band' : 'Edit price band details'
        }
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/pricing/bands')}
          >
            Back to Price Bands
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading price band" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            {/* -- Identity ----------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Identity</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                  {fieldErrors['name'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['name']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Code</span>
                  <input
                    type="text"
                    required
                    maxLength={50}
                    value={form.code}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, code: e.target.value }))
                    }
                    onBlur={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                  {fieldErrors['code'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['code']}
                    </span>
                  ) : null}
                </label>
              </div>

              {!isGeneralPublic ? (
                <div>
                  <label className={styles.toggleRow}>
                    <input
                      type="checkbox"
                      checked={form.is_default_for_new_customers}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_default_for_new_customers: e.target.checked,
                        }))
                      }
                    />
                    <span>Default for new customers</span>
                  </label>
                  <span className={styles.toggleHelp}>
                    Only one band can be the default. Enabling this will unset
                    any existing default.
                  </span>
                </div>
              ) : null}
            </div>

            {/* -- Band type ---------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Band type</h3>
              {isNew ? (
                <TypeSelector
                  value={form.type}
                  onChange={(next) =>
                    setForm((prev) => ({ ...prev, type: next }))
                  }
                />
              ) : (
                <div>
                  {form.type === 'calculated' ? (
                    <Badge variant="info">Calculated</Badge>
                  ) : (
                    <Badge variant="muted">Manual</Badge>
                  )}
                  <span className={styles.toggleHelp}>
                    Type is set at creation and cannot be changed afterwards.
                  </span>
                </div>
              )}
            </div>

            {/* -- Formula (calculated only) ------------------------ */}
            {form.type === 'calculated' ? (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Formula</h3>
                <div className={styles.grid}>
                  <label className={styles.field}>
                    <span>Operation</span>
                    <select
                      value={form.calc_op}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          calc_op: e.target.value as CalcOp,
                        }))
                      }
                      disabled={!isNew}
                    >
                      {CALC_OP_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>
                      {form.calc_op === 'plus_amount' ||
                      form.calc_op === 'minus_amount'
                        ? 'Amount (£)'
                        : 'Percentage (%)'}
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={
                        form.calc_op === 'plus_amount' ||
                        form.calc_op === 'minus_amount'
                          ? '0.01'
                          : '0.1'
                      }
                      required
                      value={form.calc_value}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          calc_value: e.target.value,
                        }))
                      }
                      disabled={!isNew}
                    />
                    {fieldErrors['calc_value'] ? (
                      <span className={styles.fieldError}>
                        {fieldErrors['calc_value']}
                      </span>
                    ) : null}
                  </label>
                </div>
                <FormulaPreview op={form.calc_op} value={form.calc_value} />
                {!isNew ? (
                  <span className={styles.toggleHelp}>
                    The formula is immutable once the band is created.
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* -- Product lines (manual only) ---------------------- */}
            {form.type === 'manual' ? (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Product lines</h3>
                {isNew ? (
                  <p className={styles.note}>
                    Save the band first, then add product lines.
                  </p>
                ) : (
                  <>
                    {lines.length === 0 ? (
                      <p className={styles.linesEmpty}>
                        No product lines yet. Click &ldquo;Add product&rdquo;
                        to start.
                      </p>
                    ) : (
                      <table className={styles.linesTable}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Unit price (£)</th>
                            <th aria-label="Remove" />
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((row, idx) => (
                            <tr key={row.product_enc_id}>
                              <td>
                                <strong>{row.product_name}</strong>{' '}
                                <span className={styles.toggleHelp}>
                                  {row.product_code}
                                </span>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className={styles.priceInput}
                                  value={row.price}
                                  onChange={(e) =>
                                    handleLinePriceChange(idx, e.target.value)
                                  }
                                  aria-label={`Price for ${row.product_name}`}
                                />
                                {fieldErrors[`line:${idx}`] ? (
                                  <span className={styles.fieldError}>
                                    {fieldErrors[`line:${idx}`]}
                                  </span>
                                ) : null}
                              </td>
                              <td className={styles.removeCell}>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  icon={X}
                                  aria-label={`Remove ${row.product_name}`}
                                  onClick={() => handleRemoveLine(idx)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className={styles.linesActions}>
                      <Button
                        type="button"
                        variant="secondary"
                        icon={Plus}
                        onClick={() => setPickerOpen(true)}
                      >
                        Add product
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew && !isGeneralPublic ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/pricing/bands')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Price Band' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete price band?"
        message={
          <p>
            Delete <strong>{form.name || 'this band'}</strong>? This cannot
            be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteMut.isPending}
      />

      {pickerOpen ? (
        <ProductPickerModal
          existing={existingProductIds}
          onCancel={() => setPickerOpen(false)}
          onSelect={handleAddLine}
        />
      ) : null}
    </div>
  );
}

// -- Product picker modal --------------------------------------------------
//
// Uses a plain `<select>` of `${name} (${code})` — same pattern Vehicles /
// Drivers used for the haulier picker. The list is limited to 100 active
// products; for a tenant with more than that, the picker shows the first
// 100 and we'll wire pagination/search in a follow-up if it becomes
// noisy.

interface ProductPickerProps {
  existing: ReadonlySet<string>;
  onSelect: (product: WbProduct) => void;
  onCancel: () => void;
}

interface ProductsResponse {
  products: WbProduct[];
}

function ProductPickerModal({
  existing,
  onSelect,
  onCancel,
}: ProductPickerProps): JSX.Element {
  const [products, setProducts] = useState<WbProduct[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<ProductsResponse>('/wb/products?per_page=100&is_active=true')
      .then((res) => {
        if (cancelled) return;
        setProducts(res.data.products);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(extractErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const available = (products ?? []).filter(
    (p) => !existing.has(p.enc_id),
  );

  function handleAdd() {
    const found = available.find((p) => p.enc_id === selectedId);
    if (found) onSelect(found);
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title="Add product line"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={selectedId === ''}
          >
            Add
          </Button>
        </>
      }
    >
      <div className={styles.modalRow}>
        {loadError ? <p className={styles.error}>{loadError}</p> : null}
        {products === null && loadError === null ? (
          <Spinner label="Loading products" />
        ) : available.length === 0 ? (
          <p className={styles.note}>
            No products available to add. All active products are already in
            this band.
          </p>
        ) : (
          <label className={styles.field}>
            <span>Product</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              aria-label="Select product"
            >
              <option value="">— Choose a product —</option>
              {available.map((p) => (
                <option key={p.enc_id} value={p.enc_id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </Modal>
  );
}
